import type { PrimaryMetricKey, PublicResult } from '../types/result';

type MetricDirection = 'higher' | 'lower';

interface ObservedMetricGroup {
  value: number;
  count: number;
  midPosition: number;
}

const overallScoreMetrics: Array<{
  key: PrimaryMetricKey;
  direction: MetricDirection;
  weight: number;
}> = [
  { key: 'sprint40Seconds', direction: 'lower', weight: 0.25 },
  { key: 'broadJumpInches', direction: 'higher', weight: 0.25 },
  { key: 'proAgilitySeconds', direction: 'lower', weight: 0.25 },
  { key: 'verticalJumpInches', direction: 'higher', weight: 0.25 },
];

function roundScore(value: number): number {
  return Math.round(value * 100) / 100;
}

function compareMetricValues(
  left: number,
  right: number,
  direction: MetricDirection,
): number {
  return direction === 'higher' ? left - right : right - left;
}

function normalizePercentile(
  position: number,
  groups: readonly ObservedMetricGroup[],
): number {
  const firstGroup = groups[0];
  const lastGroup = groups[groups.length - 1];
  if (!firstGroup || !lastGroup) {
    return 0;
  }

  const denominator = lastGroup.midPosition - firstGroup.midPosition;
  if (denominator === 0) {
    return 100;
  }

  const percentile = ((position - firstGroup.midPosition) / denominator) * 100;
  return Math.min(100, Math.max(0, percentile));
}

function buildObservedGroups(
  results: readonly PublicResult[],
  key: PrimaryMetricKey,
  direction: MetricDirection,
): ObservedMetricGroup[] {
  const values = results
    .filter((result) => !result.isProfessional)
    .map((result) => result[key])
    .filter((value): value is number => value !== null)
    .toSorted((left, right) => compareMetricValues(left, right, direction));

  const groups: ObservedMetricGroup[] = [];
  let index = 0;

  while (index < values.length) {
    const value = values[index];
    let count = 1;

    while (values[index + count] === value) {
      count += 1;
    }

    groups.push({
      value,
      count,
      midPosition: index + (count - 1) / 2,
    });
    index += count;
  }

  return groups;
}

function scoreMetricValue(
  value: number | null,
  groups: readonly ObservedMetricGroup[],
  direction: MetricDirection,
): number {
  if (value === null || groups.length === 0) {
    return 0;
  }

  if (groups.length === 1) {
    const [group] = groups;
    if (!group) {
      return 0;
    }

    return compareMetricValues(value, group.value, direction) >= 0 ? 100 : 0;
  }

  let observedCountBefore = 0;

  for (const group of groups) {
    const relation = compareMetricValues(value, group.value, direction);
    if (relation === 0) {
      return normalizePercentile(group.midPosition, groups);
    }

    if (relation < 0) {
      if (observedCountBefore === 0) {
        return 0;
      }

      return normalizePercentile(observedCountBefore - 0.5, groups);
    }

    observedCountBefore += group.count;
  }

  return 100;
}

export function assignOverallScores(
  results: readonly PublicResult[],
): PublicResult[] {
  const observedGroups = Object.fromEntries(
    overallScoreMetrics.map((metric) => [
      metric.key,
      buildObservedGroups(results, metric.key, metric.direction),
    ]),
  ) as Record<PrimaryMetricKey, ObservedMetricGroup[]>;

  return results.map((result) => {
    let weightedTotal = 0;

    for (const metric of overallScoreMetrics) {
      weightedTotal +=
        scoreMetricValue(
          result[metric.key],
          observedGroups[metric.key],
          metric.direction,
        ) * metric.weight;
    }

    return {
      ...result,
      overallScore: roundScore(weightedTotal),
    };
  });
}
